import { 
  collection, 
  query, 
  orderBy, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  onSnapshot,
  where,
  limit,
  addDoc,
  getDoc,
  setDoc,
  serverTimestamp,
  increment,
  Timestamp,
  runTransaction
} from 'firebase/firestore';
import { db } from './firebaseConfig';
import { getInvoices } from './invoiceService';

/**
 * Job Schedules Service - Handle all Firestore operations for job schedules
 */

// Helper functions for date handling
const createTimestampFilter = (date, type) => {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    console.warn(`Invalid ${type} date:`, date);
    return null;
  }
  
  try {
    return Timestamp.fromDate(date);
  } catch (error) {
    console.error(`Error converting ${type} date to timestamp:`, error);
    return null;
  }
};

const startOfDay = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    return null;
  }
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

const endOfDay = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    return null;
  }
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

// Collection references
const SCHEDULES_COLLECTION = 'jobSchedules';

// Helper functions for dual date system and manual edit protection

/**
 * Normalize date to UTC midnight format (YYYY-MM-DD)
 * @param {string|Date} dateInput - Date input to normalize
 * @returns {string|null} Normalized date string or null if invalid
 */
export const normalizeDate = (dateInput) => {
  if (!dateInput) return null;
  
  let date;
  if (typeof dateInput === 'string') {
    date = new Date(dateInput);
  } else if (dateInput instanceof Date) {
    date = new Date(dateInput);
  } else {
    return null;
  }
  
  if (isNaN(date.getTime())) return null;
  
  // Set to UTC midnight
  const utcDate = new Date(Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0, 0, 0, 0
  ));
  
  return utcDate.toISOString().split('T')[0]; // Return YYYY-MM-DD format
};

/**
 * Extract default time from invoice data
 * @param {Object} invoice - Invoice object
 * @returns {string} Default time string (HH:mm format) or empty string
 */
export const defaultTimeFrom = (invoice) => {
  // Extract time from invoice if available, otherwise return empty
  return invoice?.preferredTime || invoice?.deliveryTime || '';
};

/**
 * Generate stable schedule key for both invoice-linked and manual schedules
 * @param {Object} invoice - Invoice object (null for manual schedules)
 * @param {string} jobType - Job type
 * @param {string} phase - Optional phase identifier
 * @param {string} date - Optional date for multiple date schedules
 * @returns {string} Stable schedule key
 */
export const scheduleKey = (invoice, jobType, phase = null, date = null) => {
  if (invoice?.id) {
    // Invoice-linked schedules: invoice_id:job_type[:date][:phase]
    const baseKey = `${invoice.id}:${jobType}`;
    if (date) {
      // Include date in key for multiple date schedules
      const dateKey = `${baseKey}:${date}`;
      return phase ? `${dateKey}:${phase}` : dateKey;
    }
    return phase ? `${baseKey}:${phase}` : baseKey;
  } else {
    // Manual schedules: manual:timestamp:job_type
    return `manual:${Date.now()}:${jobType}`;
  }
};

/**
 * Derive job key for composite schedule keys
 * @param {Object} invoice - Invoice object
 * @returns {string} Job key combining job type and invoice type
 */
export const deriveJobKey = (invoice) => {
  const jobType = deriveJobType(invoice);
  return `${jobType}:${invoice.invoiceType}`;
};

/**
 * BULLETPROOF: Absolutely prevent scheduled date/time overwrites
 * This function acts as a final safety net that cannot be bypassed
 * @param {Object} existingSchedule - Current schedule data
 * @param {Object} updates - Proposed updates
 * @returns {Object} Cleaned updates with protected fields removed
 */
export const bulletproofSyncProtection = (existingSchedule, updates) => {
  const protectedFields = ['scheduledDate', 'scheduledTime'];
  const originalUpdates = { ...updates };
  
  protectedFields.forEach(field => {
    if (updates.hasOwnProperty(field)) {
      const existingValue = existingSchedule[field];
      const newValue = updates[field];
      
      // If existing schedule has this field set, NEVER allow sync to change it
      if (existingValue && existingValue !== '') {
        console.error(`🚨 BULLETPROOF PROTECTION: Preventing sync overwrite of ${field}`);
        console.error(`   Existing: ${existingValue}`);
        console.error(`   Attempted: ${newValue}`);
        console.error(`   Action: BLOCKED`);
        
        delete updates[field];
      }
    }
  });
  
  const blockedFields = Object.keys(originalUpdates).filter(key => 
    !updates.hasOwnProperty(key) && protectedFields.includes(key)
  );
  
  if (blockedFields.length > 0) {
    console.log(`🛡️ BULLETPROOF: Blocked ${blockedFields.length} field(s): ${blockedFields.join(', ')}`);
  }
  
  return updates;
};

/**
 * Derive job type from invoice data (ensure this function exists)
 * @param {Object} invoice - Invoice object
 * @returns {string} Derived job type
 */
export const deriveJobType = (invoice) => {
  if (!invoice) return JOB_TYPES.DELIVER_ONLY;

  // Check if transportation service is included
  const hasTransportationService = invoice.lineItems?.some(item =>
    item.services?.transportation
  );

  // Check if assembly/dismantle services are included
  const hasAssemblyService = invoice.lineItems?.some(item =>
    item.services?.assembly ||
    item.description?.toLowerCase().includes('assembly') ||
    item.description?.toLowerCase().includes('dismantle') ||
    item.description?.toLowerCase().includes('install')
  );

  // Determine job type based on both transportation and assembly services
  if (!hasTransportationService) {
    // Customer handles pickup/return themselves
    return JOB_TYPES.SELF_COLLECT;
  } else if (hasAssemblyService) {
    // Company provides transportation + installation
    return JOB_TYPES.DELIVER_INSTALL;
  } else {
    // Company provides transportation only
    return JOB_TYPES.DELIVER_ONLY;
  }
};

/**
 * Derive job type from services (used for unified date processing)
 * @param {boolean} hasTransportationService - Whether transportation service is included
 * @param {boolean} hasAssemblyService - Whether assembly service is included
 * @param {string} phase - Optional phase ('delivery' or 'collection')
 * @returns {string} Derived job type
 */
const deriveJobTypeFromServices = (hasTransportationService, hasAssemblyService, phase = null) => {
  if (!hasTransportationService) {
    // Customer handles pickup/return themselves
    return phase === 'collection' ? JOB_TYPES.SELF_RETURN : JOB_TYPES.SELF_COLLECT;
  } else if (hasAssemblyService) {
    // Company provides transportation + installation
    return phase === 'collection' ? JOB_TYPES.DISMANTLE_COLLECT : JOB_TYPES.DELIVER_INSTALL;
  } else {
    // Company provides transportation only
    return phase === 'collection' ? JOB_TYPES.COLLECT_ONLY : JOB_TYPES.DELIVER_ONLY;
  }
};

// Job Types Constants
export const JOB_TYPES = {
  DELIVER_INSTALL: 'Deliver & Installation',
  DISMANTLE_COLLECT: 'Dismantle & Collection',
  DELIVER_ONLY: 'Deliver Only',
  COLLECT_ONLY: 'Collect Only',
  SELF_COLLECT: 'Self Collect',
  SELF_RETURN: 'Self Return',
  SITE_SURVEY: 'Site Survey',
  SITE_INSPECTION: 'Site Inspection/Endorsement',
  SCAFFOLD_MODIFICATION: 'Scaffold Modification'
};

export const JOB_TYPE_LABELS = {
  [JOB_TYPES.DELIVER_INSTALL]: 'Deliver & Installation',
  [JOB_TYPES.DISMANTLE_COLLECT]: 'Dismantle & Collection',
  [JOB_TYPES.DELIVER_ONLY]: 'Deliver Only',
  [JOB_TYPES.COLLECT_ONLY]: 'Collect Only',
  [JOB_TYPES.SELF_COLLECT]: 'Self Collect',
  [JOB_TYPES.SELF_RETURN]: 'Self Return',
  [JOB_TYPES.SITE_SURVEY]: 'Site Survey',
  [JOB_TYPES.SITE_INSPECTION]: 'Site Inspection/Endorsement',
  [JOB_TYPES.SCAFFOLD_MODIFICATION]: 'Scaffold Modification'
};

// Schedule Status Constants
export const SCHEDULE_STATUSES = {
  CONFIRMED: 'confirmed',
  UNCONFIRMED: 'unconfirmed',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

export const SCHEDULE_STATUS_LABELS = {
  [SCHEDULE_STATUSES.CONFIRMED]: 'Confirmed',
  [SCHEDULE_STATUSES.UNCONFIRMED]: 'Unconfirmed',
  [SCHEDULE_STATUSES.COMPLETED]: 'Completed',
  [SCHEDULE_STATUSES.CANCELLED]: 'Cancelled'
};

export const SCHEDULE_STATUS_COLORS = {
  [SCHEDULE_STATUSES.CONFIRMED]: '#52c41a',     // Green
  [SCHEDULE_STATUSES.UNCONFIRMED]: '#faad14',  // Orange
  [SCHEDULE_STATUSES.COMPLETED]: '#1890ff',    // Blue
  [SCHEDULE_STATUSES.CANCELLED]: '#ff4d4f'     // Red
};

/**
 * Sync a single schedule from invoice using transactions (idempotent, merge-only)
 * @param {Object} invoice - Invoice data
 * @returns {Promise<void>} Promise that resolves when sync is complete
 */
export async function syncScheduleFromInvoice(invoice) {
  if (!invoice) {
    console.warn('⚠️ syncScheduleFromInvoice: No invoice provided');
    return;
  }

  console.log(`🔄 Syncing schedule from invoice: ${invoice.invoiceNumber}`);

  // Generate schedules that should exist for this invoice
  const expectedSchedules = generateSchedulesFromInvoice(invoice);
  
  if (expectedSchedules.length === 0) {
    console.log(`⏭️ No schedules to sync for invoice ${invoice.invoiceNumber}`);
    return;
  }

  // Process each expected schedule
  for (const expectedSchedule of expectedSchedules) {
    const scheduleId = scheduleKey(invoice, expectedSchedule.jobType);
    const ref = doc(db, SCHEDULES_COLLECTION, scheduleId);

    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      const exists = snap.exists();
      const curr = exists ? snap.data() : {};
      const manual = curr.manualEdits || {};

      console.log(`🔍 Processing schedule ${scheduleId} (exists: ${exists})`);

      const update = {
        invoiceId: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        invoiceDate: normalizeDate(expectedSchedule.invoiceDate),
        jobType: expectedSchedule.jobType,
        customerName: expectedSchedule.customerName,
        customerPhone: expectedSchedule.phone,
        companyName: expectedSchedule.companyName,
        contactPerson: expectedSchedule.contactPerson,
        siteAddress: expectedSchedule.siteAddress,
        configuration: expectedSchedule.configuration,
        lastSyncedAt: new Date()
      };

      if (!exists) {
        // New schedule - NEVER create scheduledDate/scheduledTime during sync
        console.log(`✨ Creating new schedule - NO scheduled fields during sync`);
        update.status = SCHEDULE_STATUSES.CONFIRMED;
        update.manualEdits = { 
          scheduledDate: false, 
          scheduledTime: false, 
          notes: false 
        };
        // Maintain backward compatibility
        update.editedFields = [];
        update.hasManualEdits = false;
        update.jobDate = normalizeDate(expectedSchedule.invoiceDate); // Legacy field
        update.notes = expectedSchedule.notes || '';
        update.isManualEntry = false;
        update.createdAt = new Date();
        
        // CRITICAL: NEVER set scheduledDate or scheduledTime during sync
        // These fields should ONLY be set by manual user input via saveManualScheduleEdit
        console.log(`🔒 SYNC PROTECTION: scheduledDate and scheduledTime will remain empty until user manually sets them`);
      } else {
        // Existing schedule - ABSOLUTELY NEVER touch scheduledDate/scheduledTime during sync
        console.log(`🔄 Updating existing schedule - NEVER touching scheduled fields during sync`);
        
        // CRITICAL: Do not check or modify scheduledDate/scheduledTime at all during sync
        // These fields are completely off-limits to sync operations
        console.log(`🔒 SYNC PROTECTION: scheduledDate (${curr.scheduledDate || 'empty'}) and scheduledTime (${curr.scheduledTime || 'empty'}) are completely protected`);

        // Always update legacy jobDate for backward compatibility
        update.jobDate = normalizeDate(expectedSchedule.invoiceDate);
      }

      // Use merge to avoid overwriting protected fields
      tx.set(ref, update, { merge: true });
      console.log(`✅ Schedule ${scheduleId} synced successfully`);
    });
  }
}

/**
 * Save manual schedule edits with proper manualEdits tracking
 * @param {string} scheduleId - Schedule ID
 * @param {Object} edits - Manual edits { scheduledDate?, scheduledTime?, notes? }
 * @returns {Promise<void>} Promise that resolves when save is complete
 */
export async function saveManualScheduleEdit(scheduleId, { scheduledDate, scheduledTime, notes }) {
  if (!scheduleId) {
    throw new Error('Schedule ID is required');
  }

  console.log(`✏️ Saving manual edits for schedule ${scheduleId}:`, {
    scheduledDate: scheduledDate !== undefined ? scheduledDate : 'unchanged',
    scheduledTime: scheduledTime !== undefined ? scheduledTime : 'unchanged', 
    notes: notes !== undefined ? 'updated' : 'unchanged'
  });

  const ref = doc(db, SCHEDULES_COLLECTION, scheduleId);
  
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new Error(`Schedule ${scheduleId} not found`);
    }

    const curr = snap.data();
    const existingManualEdits = curr.manualEdits || {};
    
    const update = { 
      lastManualEditAt: new Date(),
      updatedAt: new Date(),
      manualEdits: { ...existingManualEdits }
    };

    // Track which fields were manually edited
    if (scheduledDate !== undefined) {
      update.scheduledDate = normalizeDate(scheduledDate);
      update.manualEdits.scheduledDate = true;
      update.jobDate = normalizeDate(scheduledDate); // Update legacy field
      console.log(`📅 Manual scheduledDate edit: ${scheduledDate}`);
    }
    
    if (scheduledTime !== undefined) {
      update.scheduledTime = scheduledTime;
      update.manualEdits.scheduledTime = true;
      console.log(`⏰ Manual scheduledTime edit: ${scheduledTime}`);
    }
    
    if (notes !== undefined) {
      update.notes = notes;
      update.manualEdits.notes = true;
      console.log(`📝 Manual notes edit`);
    }

    // Maintain backward compatibility with editedFields array
    const editedFields = curr.editedFields || [];
    if (scheduledDate !== undefined && !editedFields.includes('scheduledDate')) {
      editedFields.push('scheduledDate');
    }
    if (scheduledTime !== undefined && !editedFields.includes('scheduledTime')) {
      editedFields.push('scheduledTime');
    }
    if (notes !== undefined && !editedFields.includes('notes')) {
      editedFields.push('notes');
    }
    
    update.editedFields = editedFields;
    update.hasManualEdits = true;

    // Use merge to preserve other fields
    tx.set(ref, update, { merge: true });
    console.log(`✅ Manual edits saved for schedule ${scheduleId}`);
  });
}

/**
 * Generate schedules from invoice data with dual date system
 * @param {Object} invoice - Invoice data
 * @returns {Array} Array of schedule objects to create
 */
export const generateSchedulesFromInvoice = (invoice) => {
  const schedules = [];

  if (!invoice) {
    console.warn('⚠️ generateSchedulesFromInvoice: No invoice provided');
    return schedules;
  }

  console.log(`🔍 Generating schedules for invoice: ${invoice.invoiceNumber} (${invoice.invoiceType})`);
  console.log(`🔍 Invoice data:`, {
    id: invoice.id,
    dates: invoice.dates,
    lineItems: invoice.lineItems,
    invoiceType: invoice.invoiceType
  });

  // Extract customer information with better fallbacks
  const customerInfo = {
    customerName: invoice.attn || invoice.companyName || invoice.customerName || 'Unknown Customer',
    companyName: invoice.companyName || invoice.customerName || 'Unknown Company',
    contactPerson: invoice.attn || invoice.contactPerson || 'Unknown Contact',
    phone: invoice.tel || invoice.phone || 'No phone provided',
    siteAddress: invoice.siteAddress || invoice.address || 'Address not specified'
  };

  console.log(`👤 Customer info: ${customerInfo.companyName} - ${customerInfo.contactPerson}`);

  // Extract configuration from line items with better error handling
  // Convert working height to platform height (platform height = working height - 2m)
  // Only include line items that have valid working height data
  let configuration = 'Configuration not specified';
  
  try {
    if (invoice.lineItems && Array.isArray(invoice.lineItems) && invoice.lineItems.length > 0) {
      // Filter line items to only include those with valid working height
      const validLineItems = invoice.lineItems.filter(item => {
        if (!item.workingHeight) {
          console.log(`⏭️ Skipping line item without working height: ${JSON.stringify(item)}`);
          return false;
        }
        
        const workingHeight = parseFloat(item.workingHeight);
        if (isNaN(workingHeight) || workingHeight < 2) {
          console.warn(`⚠️ Skipping line item with invalid working height: ${item.workingHeight}m (must be ≥2m)`);
          return false;
        }
        
        return true;
      });
      
      console.log(`📊 Filtered line items: ${validLineItems.length} valid out of ${invoice.lineItems.length} total`);
      
      if (validLineItems.length > 0) {
        configuration = validLineItems.map(item => {
          const width = item.width || 'single';
          const widthLabel = width === 'double' ? 'Double' : 'Single';
          
          // Convert working height to platform height (subtract 2m)
          const workingHeight = parseFloat(item.workingHeight);
          const platformHeight = workingHeight - 2;
          const heightDisplay = `${platformHeight}m`;
          
          console.log(`📏 Height conversion: Working ${workingHeight}m → Platform ${platformHeight}m`);
          
          return `${heightDisplay} Platform ${widthLabel}`;
        }).join(', ');
        console.log(`🔧 Generated configuration: ${configuration}`);
      } else {
        configuration = 'Configuration incomplete - missing working heights';
        console.log('⚠️ No valid line items with working height found');
      }
    } else {
      console.log('⚠️ No line items found, using default configuration');
    }
  } catch (error) {
    console.error('❌ Error processing line items:', error);
  }

  // Generate schedules based on invoice type and dates
  console.log(`📅 Processing ${invoice.invoiceType} invoice...`);
  console.log(`📅 Invoice dates array:`, invoice.dates);

  // DAMAGED_ITEMS invoices should never generate schedules
  if (invoice.invoiceType === 'damaged_items') {
    console.log('⏭️ DAMAGED_ITEMS invoice - no schedules will be generated');
    return schedules; // Return empty array
  }

  // Check if transportation service is included (used for all invoice types)
  const hasTransportationService = invoice.lineItems?.some(item =>
    item.services?.transportation
  );

  // Check if assembly/dismantle services are included (used for all invoice types)
  const hasAssemblyService = invoice.lineItems?.some(item =>
    item.services?.assembly ||
    item.description?.toLowerCase().includes('assembly') ||
    item.description?.toLowerCase().includes('dismantle')
  );

  console.log(`🚚 Has transportation service: ${hasTransportationService}`);
  console.log(`🔧 Has assembly service: ${hasAssemblyService}`);

  // Process the new unified dates array
  if (invoice.dates && Array.isArray(invoice.dates) && invoice.dates.length > 0) {
    console.log(`📅 Processing ${invoice.dates.length} date entries from unified dates array`);

    invoice.dates.forEach((dateEntry, index) => {
      console.log(`📅 Processing date entry ${index + 1}:`, dateEntry);

      // SPECIAL HANDLING FOR SCAFFOLDING SERVICES: Use scaffolding-specific job type logic
      let jobType;
      if (invoice.invoiceType === 'scaffolding_service') {
        // Check if only "Weekly inspection" service is selected
        const hasOnlyWeeklyInspection = invoice.lineItems?.some(item => {
          const description = item.description?.toLowerCase() || '';
          const hasWeeklyInspection = description.includes('weekly') && description.includes('inspection');
          const hasOtherServices = description.includes('assembly') ||
                                 description.includes('dismantle') ||
                                 description.includes('modification') ||
                                 description.includes('erection') ||
                                 description.includes('removal') ||
                                 (item.services?.assembly) ||
                                 (item.services?.transportation);

          return hasWeeklyInspection && !hasOtherServices;
        });

        if (hasOnlyWeeklyInspection) {
          jobType = JOB_TYPES.SITE_INSPECTION;
        } else {
          // Any other scaffolding service (assembly, dismantle, transportation, etc.)
          jobType = JOB_TYPES.SCAFFOLD_MODIFICATION;
        }
      }

      switch (dateEntry.type) {
        case 'date':
          // Single date → 1 confirmed schedule
          if (dateEntry.value) {
            // Use scaffolding-specific job type if applicable, otherwise use general logic
            const finalJobType = jobType || deriveJobTypeFromServices(hasTransportationService, hasAssemblyService);
            const schedule = {
              invoiceId: invoice.id,
              invoiceNumber: invoice.invoiceNumber,
              invoiceDate: dateEntry.value,
              jobDate: dateEntry.value,
              jobType: finalJobType,
              ...customerInfo,
              configuration,
              status: SCHEDULE_STATUSES.CONFIRMED,
              isManualEntry: false,
              notes: `Single date schedule - ${dateEntry.value}`
            };
            schedules.push(schedule);
            console.log(`✅ Created single date schedule: ${dateEntry.value} - ${finalJobType}`);
          }
          break;

        case 'range':
          // Date range → 2 confirmed schedules (start + end dates only)
          if (dateEntry.value && dateEntry.endValue) {
            // Determine job types for delivery and collection
            let deliveryJobType, collectionJobType;
            if (jobType) {
              // For scaffolding services, use the same job type for both delivery and collection
              deliveryJobType = jobType;
              collectionJobType = jobType;
            } else {
              deliveryJobType = deriveJobTypeFromServices(hasTransportationService, hasAssemblyService, 'delivery');
              collectionJobType = deriveJobTypeFromServices(hasTransportationService, hasAssemblyService, 'collection');
            }

            // Start date schedule
            const startSchedule = {
              invoiceId: invoice.id,
              invoiceNumber: invoice.invoiceNumber,
              invoiceDate: dateEntry.value,
              jobDate: dateEntry.value,
              jobType: deliveryJobType,
              ...customerInfo,
              configuration,
              status: SCHEDULE_STATUSES.CONFIRMED,
              isManualEntry: false,
              notes: `Date range start: ${dateEntry.value} to ${dateEntry.endValue}`
            };
            schedules.push(startSchedule);
            console.log(`✅ Created range start schedule: ${dateEntry.value} - ${deliveryJobType}`);

            // End date schedule
            const endSchedule = {
              invoiceId: invoice.id,
              invoiceNumber: invoice.invoiceNumber,
              invoiceDate: dateEntry.endValue,
              jobDate: dateEntry.endValue,
              jobType: collectionJobType,
              ...customerInfo,
              configuration,
              status: SCHEDULE_STATUSES.CONFIRMED,
              isManualEntry: false,
              notes: `Date range end: ${dateEntry.value} to ${dateEntry.endValue}`
            };
            schedules.push(endSchedule);
            console.log(`✅ Created range end schedule: ${dateEntry.endValue} - ${collectionJobType}`);
          }
          break;

        case 'tbc':
          // TBC → 1 unconfirmed schedule
          const finalTbcJobType = jobType || deriveJobTypeFromServices(hasTransportationService, hasAssemblyService);
          const tbcSchedule = {
            invoiceId: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            invoiceDate: null,
            jobDate: null,
            jobType: finalTbcJobType,
            ...customerInfo,
            configuration,
            status: SCHEDULE_STATUSES.UNCONFIRMED,
            isManualEntry: false,
            notes: `Date to be confirmed`
          };
          schedules.push(tbcSchedule);
          console.log(`✅ Created TBC schedule: ${finalTbcJobType}`);
          break;

        default:
          console.log(`⚠️ Unknown date entry type: ${dateEntry.type}`);
      }
    });
  } else {
    console.log('❌ No unified dates found in invoice - checking legacy fields');

    // Fallback to legacy date processing for backward compatibility
    switch (invoice.invoiceType) {
      case 'rental':
      case 'rental_extension':
        console.log(`📅 Rental dates - Start: ${invoice.rentalStartDate}, End: ${invoice.rentalEndDate}`);

        // For rental with date range - generate both delivery and collection
        if (invoice.rentalStartDate && invoice.rentalEndDate) {
          console.log('✅ Both rental dates found, generating schedules...');

          // Determine job types based on both transportation and assembly services
          let deliveryJobType, collectionJobType;

          if (!hasTransportationService) {
            // Customer handles pickup/return themselves
            deliveryJobType = JOB_TYPES.SELF_COLLECT;
            collectionJobType = JOB_TYPES.SELF_RETURN;
          } else if (hasAssemblyService) {
            // Company provides transportation + installation
            deliveryJobType = JOB_TYPES.DELIVER_INSTALL;
            collectionJobType = JOB_TYPES.DISMANTLE_COLLECT;
          } else {
            // Company provides transportation only
            deliveryJobType = JOB_TYPES.DELIVER_ONLY;
            collectionJobType = JOB_TYPES.COLLECT_ONLY;
          }

          // Create delivery schedule - ONLY invoice-related fields (NO scheduled fields)
          const deliverySchedule = {
            invoiceId: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            // ONLY invoice date (read-only from invoice)
            invoiceDate: invoice.rentalStartDate,
            // NEVER include scheduledDate or scheduledTime in generated schedules
            // These fields should ONLY come from manual user input
            // Legacy field for backward compatibility (will be removed later)
            jobDate: invoice.rentalStartDate,
            jobType: deliveryJobType,
            ...customerInfo,
            configuration,
            status: SCHEDULE_STATUSES.CONFIRMED,
            isManualEntry: false,
            notes: `Rental period: ${invoice.rentalStartDate} to ${invoice.rentalEndDate}`
          };
          schedules.push(deliverySchedule);
          console.log(`✅ Created delivery schedule: ${deliveryJobType}`);

          // Create collection schedule - ONLY invoice-related fields (NO scheduled fields)
          const collectionSchedule = {
            invoiceId: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            // ONLY invoice date (read-only from invoice)
            invoiceDate: invoice.rentalEndDate,
            // NEVER include scheduledDate or scheduledTime in generated schedules
            // These fields should ONLY come from manual user input
            // Legacy field for backward compatibility (will be removed later)
            jobDate: invoice.rentalEndDate,
            jobType: collectionJobType,
            ...customerInfo,
            configuration,
            status: SCHEDULE_STATUSES.CONFIRMED,
            isManualEntry: false,
            notes: `End of rental period`
          };
          schedules.push(collectionSchedule);
          console.log(`✅ Created collection schedule: ${collectionJobType}`);
        } else {
          console.log('❌ Missing rental dates - no schedules generated');
          console.log(`   Start date: ${invoice.rentalStartDate || 'MISSING'}`);
          console.log(`   End date: ${invoice.rentalEndDate || 'MISSING'}`);
        }
        break;

      case 'purchase':
        console.log(`📅 Purchase lead time: ${invoice.leadTime}`);

        // For purchase with lead time
        if (invoice.leadTime) {
          // Determine job type based on transportation service
          const jobType = hasTransportationService ? JOB_TYPES.DELIVER_ONLY : JOB_TYPES.SELF_COLLECT;

          const purchaseSchedule = {
            invoiceId: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            // ONLY invoice date (read-only from invoice)
            invoiceDate: invoice.leadTime,
            // NEVER include scheduledDate or scheduledTime in generated schedules
            // These fields should ONLY come from manual user input
            // Legacy field for backward compatibility (will be removed later)
            jobDate: invoice.leadTime,
            jobType: jobType,
            ...customerInfo,
            configuration,
            status: SCHEDULE_STATUSES.CONFIRMED,
            isManualEntry: false,
            notes: `Purchase delivery`
          };
          schedules.push(purchaseSchedule);
          console.log(`✅ Created PURCHASE schedule: ${jobType}`);
        } else {
          console.log('❌ Missing lead time - no schedule generated');
        }
        break;

      case 'scaffolding_service':
        console.log(`🏗️ Processing scaffolding service invoice`);

        // For scaffolding services, determine job type based on services selected
        if (invoice.serviceDate) {
          let jobType;

          // Check if only "Weekly inspection" service is selected
          const hasOnlyWeeklyInspection = invoice.lineItems?.some(item => {
            const description = item.description?.toLowerCase() || '';
            const hasWeeklyInspection = description.includes('weekly') && description.includes('inspection');
            const hasOtherServices = description.includes('assembly') ||
                                   description.includes('dismantle') ||
                                   description.includes('modification') ||
                                   description.includes('erection') ||
                                   description.includes('removal') ||
                                   (item.services?.assembly) ||
                                   (item.services?.transportation);

            return hasWeeklyInspection && !hasOtherServices;
          });

          // Check for assembly/dismantle services specifically for scaffolding
          const hasScaffoldingAssembly = invoice.lineItems?.some(item => {
            const description = item.description?.toLowerCase() || '';
            return description.includes('assembly') ||
                   description.includes('dismantle') ||
                   description.includes('erection') ||
                   description.includes('removal') ||
                   (item.services?.assembly);
          });

          console.log(`🔍 Scaffolding service analysis:`, {
            hasOnlyWeeklyInspection,
            hasScaffoldingAssembly,
            hasTransportationService,
            hasAssemblyService,
            lineItems: invoice.lineItems?.map(item => ({
              description: item.description,
              services: item.services
            }))
          });

          if (hasOnlyWeeklyInspection) {
            // Only weekly inspection selected
            jobType = JOB_TYPES.SITE_INSPECTION;
            console.log(`✅ Weekly inspection only - using: ${jobType}`);
          } else {
            // Any other scaffolding service (assembly, dismantle, transportation, etc.)
            // For scaffolding services, default to Scaffold Modification
            jobType = JOB_TYPES.SCAFFOLD_MODIFICATION;
            console.log(`✅ Scaffolding service (non-weekly) - using: ${jobType}`);
          }

          const serviceSchedule = {
            invoiceId: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            // ONLY invoice date (read-only from invoice)
            invoiceDate: invoice.serviceDate,
            // NEVER include scheduledDate or scheduledTime in generated schedules
            // These fields should ONLY come from manual user input
            // Legacy field for backward compatibility (will be removed later)
            jobDate: invoice.serviceDate,
            jobType: jobType,
            ...customerInfo,
            configuration,
            status: SCHEDULE_STATUSES.CONFIRMED,
            isManualEntry: false,
            notes: `Scaffolding service - ${jobType}`
          };
          schedules.push(serviceSchedule);
          console.log(`✅ Created SCAFFOLDING SERVICE schedule: ${jobType}`);
        } else {
          console.log('❌ Missing service date - no schedule generated');
        }
        break;

      default:
        console.log(`❌ Unknown invoice type: ${invoice.invoiceType}`);
    }
  }

  console.log(`📊 Generated ${schedules.length} schedule(s) for ${invoice.invoiceNumber}`);
  return schedules;
};

/**
 * Create a new schedule
 * @param {Object} scheduleData - Schedule data
 * @returns {Promise<Object>} Created schedule with ID
 */
export const createSchedule = async (scheduleData) => {
  try {
    console.log(`🔍 Creating schedule with data:`, scheduleData);

    const schedule = {
      ...scheduleData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    console.log(`🔍 Final schedule object:`, schedule);

    const docRef = await addDoc(collection(db, SCHEDULES_COLLECTION), schedule);

    console.log(`✅ Schedule created successfully with ID: ${docRef.id}`);

    return {
      id: docRef.id,
      ...schedule
    };
  } catch (error) {
    console.error('❌ Error creating schedule:', error);
    console.error('❌ Schedule data that failed:', scheduleData);
    throw error;
  }
};

/**
 * Update an existing schedule
 * @param {string} scheduleId - Schedule ID
 * @param {Object} updates - Updated fields
 * @param {Object} options - Update options
 * @returns {Promise<boolean>} Success status
 */
export const updateSchedule = async (scheduleId, updates, options = {}) => {
  try {
    const scheduleRef = doc(db, SCHEDULES_COLLECTION, scheduleId);
    
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp()
    };
    
    // If this is a manual edit, track the edited fields
    if (options.isManualEdit && options.editedFields) {
      updateData.editedFields = options.editedFields;
      updateData.lastEditedAt = serverTimestamp();
      updateData.hasManualEdits = true;
    }
    
    await updateDoc(scheduleRef, updateData);
    return true;
  } catch (error) {
    console.error('Error updating schedule:', error);
    return false;
  }
};

/**
 * Update schedule with dual date system (preserves scheduled dates, syncs invoice dates)
 * @param {string} scheduleId - Schedule ID
 * @param {Object} existingSchedule - Current schedule data
 * @param {Object} expectedData - Expected data from invoice
 * @returns {Promise<boolean>} Success status
 */
export const updateScheduleSelective = async (scheduleId, existingSchedule, expectedData) => {
  try {
    // Validate inputs
    if (!scheduleId || !existingSchedule || !expectedData) {
      console.error('❌ updateScheduleSelective: Missing required parameters', {
        scheduleId: !!scheduleId,
        existingSchedule: !!existingSchedule,
        expectedData: !!expectedData
      });
      return false;
    }

    const updates = {};
    
    console.log(`🔍 Dual date sync for schedule ${scheduleId}:`, {
      invoiceNumber: expectedData.invoiceNumber,
      currentInvoiceDate: existingSchedule.invoiceDate,
      newInvoiceDate: expectedData.invoiceDate,
      currentScheduledDate: existingSchedule.scheduledDate,
      currentScheduledTime: existingSchedule.scheduledTime
    });
    
    // DUAL DATE SYSTEM: Always sync invoice date (read-only from invoice)
    if (expectedData.invoiceDate !== undefined) {
      const currentInvoiceDate = existingSchedule.invoiceDate;
      const newInvoiceDate = expectedData.invoiceDate;
      
      if (currentInvoiceDate !== newInvoiceDate) {
        updates.invoiceDate = newInvoiceDate;
        console.log(`📅 Invoice date sync: "${currentInvoiceDate}" → "${newInvoiceDate}"`);
        
        // Also update legacy jobDate for backward compatibility
        updates.jobDate = newInvoiceDate;
      }
    }
    
    // CRITICAL FIX: ABSOLUTELY NEVER sync scheduled date/time during sync operations
    // These fields are ONLY for manual user scheduling and must be completely protected
    console.log(`🔒 PROTECTED FIELDS - Scheduled date/time will NEVER be updated during sync:`);
    console.log(`   Current scheduledDate: ${existingSchedule.scheduledDate || 'not set'}`);
    console.log(`   Current scheduledTime: ${existingSchedule.scheduledTime || 'not set'}`);
    console.log(`   Expected scheduledDate: ${expectedData.scheduledDate || 'not set'} (IGNORED)`);
    console.log(`   Expected scheduledTime: ${expectedData.scheduledTime || 'not set'} (IGNORED)`);
    
    // Explicitly exclude scheduledDate and scheduledTime from any updates
    // These should NEVER appear in the updates object during sync
    
    // Sync other invoice-related fields (but respect manual edits for non-date fields)
    const editedFields = existingSchedule.editedFields || [];
    
    // Define which non-date fields can be manually edited and should be preserved
    const protectableFields = [
      'customerName',
      'companyName', 
      'contactPerson',
      'phone',
      'siteAddress',
      'configuration',
      'notes',
      'status'
    ];
    
    // Define fields that should always sync from invoice (never protected)
    const alwaysSyncFields = [
      'invoiceNumber',
      'invoiceId',
      'jobType' // Job type should sync from invoice logic
    ];
    
    // CRITICAL: Define fields that are ABSOLUTELY FORBIDDEN during sync
    const forbiddenSyncFields = [
      'scheduledDate',
      'scheduledTime'
    ];
    
    // CRITICAL VALIDATION: Remove forbidden fields from expectedData completely
    forbiddenSyncFields.forEach(field => {
      if (expectedData.hasOwnProperty(field)) {
        console.log(`🚨 CRITICAL: Removing forbidden field ${field} from expectedData during sync`);
        console.log(`   Expected value: ${expectedData[field]} (REMOVED)`);
        console.log(`   Current value: ${existingSchedule[field]} (PRESERVED)`);
        delete expectedData[field]; // Remove the field completely
      }
    });
    
    // Only update protectable fields that haven't been manually edited
    protectableFields.forEach(field => {
      // Skip forbidden fields completely
      if (forbiddenSyncFields.includes(field)) {
        console.log(`🚫 Skipping forbidden sync field: ${field}`);
        return;
      }
      
      if (!editedFields.includes(field) && expectedData[field] !== undefined) {
        const currentValue = existingSchedule[field];
        const newValue = expectedData[field];
        
        // Do string comparison with null/undefined handling
        const currentStr = (currentValue || '').toString().trim();
        const newStr = (newValue || '').toString().trim();
        const isDifferent = currentStr !== newStr;
        
        if (isDifferent) {
          updates[field] = newValue;
          console.log(`📝 Will update ${field}: "${currentValue}" → "${newValue}"`);
        }
      } else if (editedFields.includes(field)) {
        console.log(`🔒 Protected ${field}: keeping manual edit "${existingSchedule[field]}"`);
      }
    });
    
    // Always update these fields from invoice (with change detection)
    alwaysSyncFields.forEach(field => {
      // Skip forbidden fields completely
      if (forbiddenSyncFields.includes(field)) {
        console.log(`🚫 Skipping forbidden sync field: ${field}`);
        return;
      }
      
      if (expectedData[field] !== undefined) {
        const currentValue = existingSchedule[field];
        const newValue = expectedData[field];
        
        if (currentValue !== newValue) {
          updates[field] = newValue;
          console.log(`🔄 Always sync ${field}: "${currentValue}" → "${newValue}"`);
        }
      }
    });
    
    // Final validation: Ensure forbidden fields are never in updates
    forbiddenSyncFields.forEach(field => {
      if (updates.hasOwnProperty(field)) {
        console.error(`🚨 CRITICAL ERROR: Forbidden field ${field} found in updates! Removing it.`);
        delete updates[field];
      }
    });
    
    // BULLETPROOF PROTECTION: Apply final safety net
    const protectedUpdates = bulletproofSyncProtection(existingSchedule, updates);
    
    // Use the protected updates instead of the original ones
    Object.keys(updates).forEach(key => delete updates[key]);
    Object.assign(updates, protectedUpdates);
    
    // Only update if there are actual changes
    if (Object.keys(updates).length > 0) {
      updates.updatedAt = serverTimestamp();
      updates.lastSyncedAt = serverTimestamp();
      
      const scheduleRef = doc(db, SCHEDULES_COLLECTION, scheduleId);
      await updateDoc(scheduleRef, updates);
      
      console.log(`✅ Dual date sync completed for schedule ${scheduleId}:`, {
        updated: Object.keys(updates).filter(key => !['updatedAt', 'lastSyncedAt'].includes(key)),
        protected: ['scheduledDate', 'scheduledTime', ...editedFields],
        totalChanges: Object.keys(updates).length - 2 // Exclude timestamp fields
      });
    } else {
      console.log(`⏭️ No changes needed for schedule ${scheduleId} - all fields up to date`);
    }
    
    return true;
  } catch (error) {
    console.error(`❌ Error updating schedule ${scheduleId} with dual date sync:`, error);
    return false;
  }
};

/**
 * Get all schedules with real-time updates (supports dual date system)
 * @param {Function} callback - Function to call when data updates
 * @param {Object} options - Query options (status, startDate, endDate, limit, orderBy, includeUnconfirmed)
 * @returns {Function} Unsubscribe function
 */
export const subscribeToSchedules = (callback, options = {}) => {
  try {
    let q = collection(db, SCHEDULES_COLLECTION);

    // Add filters
    if (options.status) {
      q = query(q, where('status', '==', options.status));
    }

    if (options.invoiceId) {
      q = query(q, where('invoiceId', '==', options.invoiceId));
    }

    // Filter out past dates by default (unless explicitly requested)
    // Use scheduledDate if available, fallback to jobDate for backward compatibility
    if (!options.includePastDates && !options.includeAllUnconfirmed) {
      // Use Singapore timezone (UTC+8) instead of UTC for "today" calculation
      const now = new Date();
      const singaporeOffset = 8 * 60; // 8 hours in minutes
      const singaporeTime = new Date(now.getTime() + (singaporeOffset * 60 * 1000));
      const today = singaporeTime.toISOString().split('T')[0];
      // Note: We still use jobDate for querying since Firestore needs indexed fields
      // The client-side filtering will handle the dual date logic
      q = query(q, where('jobDate', '>=', today));
    }

    // Add date range filters
    if (options.startDate) {
      const startTimestamp = createTimestampFilter(startOfDay(options.startDate), 'start');
      if (startTimestamp) {
        q = query(q, where('jobDate', '>=', options.startDate.toISOString().split('T')[0]));
      }
    }
    if (options.endDate) {
      const endTimestamp = createTimestampFilter(endOfDay(options.endDate), 'end');
      if (endTimestamp) {
        q = query(q, where('jobDate', '<=', options.endDate.toISOString().split('T')[0]));
      }
    }

    // Add ordering - still use jobDate for database ordering
    const orderField = options.orderBy || 'jobDate';
    const orderDirection = options.orderDirection || 'asc';
    q = query(q, orderBy(orderField, orderDirection));

    // Add limit
    if (options.limit) {
      q = query(q, limit(options.limit));
    }

    return onSnapshot(q, (snapshot) => {
      const schedules = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        schedules.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        });
      });

      // Client-side filtering for dual date system
      let filteredSchedules = schedules;

      if (!options.includePastDates) {
        // Use Singapore timezone (UTC+8) instead of UTC for date calculations
        const now = new Date();
        const singaporeOffset = 8 * 60; // 8 hours in minutes
        const singaporeTime = new Date(now.getTime() + (singaporeOffset * 60 * 1000));
        const today = singaporeTime.toISOString().split('T')[0];
        filteredSchedules = schedules.filter(schedule => {
          // If includeAllUnconfirmed is true, always include ALL unconfirmed schedules regardless of date
          if (options.includeAllUnconfirmed && schedule.status === SCHEDULE_STATUSES.UNCONFIRMED) {
            return true;
          }

          // For confirmed schedules, use scheduledDate if available, fallback to jobDate
          const displayDate = schedule.scheduledDate || schedule.jobDate;
          return displayDate >= today;
        });
      }

      // Sort by scheduled date and time if using dual date system
      filteredSchedules.sort((a, b) => {
        const dateA = a.scheduledDate || a.jobDate;
        const dateB = b.scheduledDate || b.jobDate;
        const timeA = a.scheduledTime || a.jobTime || '';
        const timeB = b.scheduledTime || b.jobTime || '';

        // First compare by date
        const dateComparison = options.orderDirection === 'desc' ?
          dateB.localeCompare(dateA) : dateA.localeCompare(dateB);

        // If dates are the same, compare by time
        if (dateComparison === 0) {
          // Handle empty times (put them at the end of the day)
          if (!timeA && !timeB) return 0;
          if (!timeA) return 1;  // Schedule without time goes after those with time
          if (!timeB) return -1; // Schedule without time goes after those with time

          // Compare times (both are in HH:mm format)
          return options.orderDirection === 'desc' ?
            timeB.localeCompare(timeA) : timeA.localeCompare(timeB);
        }

        return dateComparison;
      });

      callback(filteredSchedules);
    }, (error) => {
      console.error('Error fetching schedules:', error);
      callback([]);
    });
  } catch (error) {
    console.error('Error setting up schedules subscription:', error);
    callback([]);
    return () => {};
  }
};

/**
 * Get schedules once (without real-time updates)
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Array of schedules
 */
export const getSchedules = async (options = {}) => {
  try {
    let q = collection(db, SCHEDULES_COLLECTION);
    
    if (options.status) {
      q = query(q, where('status', '==', options.status));
    }
    
    // Filter out past dates by default
    if (!options.includePastDates) {
      // Use Singapore timezone (UTC+8) instead of UTC for "today" calculation
      const now = new Date();
      const singaporeOffset = 8 * 60; // 8 hours in minutes
      const singaporeTime = new Date(now.getTime() + (singaporeOffset * 60 * 1000));
      const today = singaporeTime.toISOString().split('T')[0];
      q = query(q, where('jobDate', '>=', today));
    }
    
    const orderField = options.orderBy || 'jobDate';
    const orderDirection = options.orderDirection || 'asc';
    q = query(q, orderBy(orderField, orderDirection));
    
    if (options.limit) {
      q = query(q, limit(options.limit));
    }

    const snapshot = await getDocs(q);
    const schedules = [];
    
    snapshot.forEach((doc) => {
      schedules.push({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      });
    });
    
    return schedules;
  } catch (error) {
    console.error('Error getting schedules:', error);
    return [];
  }
};

/**
 * Get schedules by invoice ID
 * @param {string} invoiceId - Invoice ID to filter by
 * @param {Object} options - Additional query options
 * @returns {Promise<Array>} Array of schedules for the invoice
 */
export const getSchedulesByInvoiceId = async (invoiceId, options = {}) => {
  try {
    if (!invoiceId) {
      console.warn('getSchedulesByInvoiceId: No invoiceId provided');
      return [];
    }

    console.log(`📅 Getting schedules for invoice: ${invoiceId}`);
    
    let q = collection(db, SCHEDULES_COLLECTION);
    
    // Filter by invoice ID
    q = query(q, where('invoiceId', '==', invoiceId));
    
    // Add additional filters if provided
    if (options.status) {
      q = query(q, where('status', '==', options.status));
    }
    
    // Include past dates by default for invoice-specific queries
    if (!options.includePastDates && options.excludePastDates) {
      const now = new Date();
      const singaporeOffset = 8 * 60; // 8 hours in minutes
      const singaporeTime = new Date(now.getTime() + (singaporeOffset * 60 * 1000));
      const today = singaporeTime.toISOString().split('T')[0];
      q = query(q, where('jobDate', '>=', today));
    }
    
    // Order by date
    const orderField = options.orderBy || 'jobDate';
    const orderDirection = options.orderDirection || 'asc';
    q = query(q, orderBy(orderField, orderDirection));
    
    if (options.limit) {
      q = query(q, limit(options.limit));
    }

    const snapshot = await getDocs(q);
    const schedules = [];
    
    snapshot.forEach((doc) => {
      schedules.push({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      });
    });
    
    console.log(`✅ Found ${schedules.length} schedules for invoice ${invoiceId}`);
    return schedules;
    
  } catch (error) {
    console.error(`❌ Error getting schedules for invoice ${invoiceId}:`, error);
    return [];
  }
};

/**
 * Delete a schedule
 * @param {string} scheduleId - Schedule ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteSchedule = async (scheduleId) => {
  try {
    await deleteDoc(doc(db, SCHEDULES_COLLECTION, scheduleId));
    return true;
  } catch (error) {
    console.error('Error deleting schedule:', error);
    return false;
  }
};

/**
 * Delete all schedules associated with an invoice
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<Object>} Result with count of deleted schedules
 */
export const deleteSchedulesByInvoiceId = async (invoiceId) => {
  try {
    console.log(`🗑️ Deleting schedules for invoice: ${invoiceId}`);
    
    // Query for all schedules with this invoice ID
    const q = query(
      collection(db, SCHEDULES_COLLECTION),
      where('invoiceId', '==', invoiceId)
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      console.log(`✅ No schedules found for invoice ${invoiceId}`);
      return { success: true, deletedCount: 0 };
    }
    
    console.log(`📅 Found ${snapshot.size} schedule(s) to delete for invoice ${invoiceId}`);
    
    // Delete all matching schedules
    const deletePromises = [];
    snapshot.forEach((doc) => {
      console.log(`🗑️ Deleting schedule: ${doc.id} (${doc.data().jobDate} - ${doc.data().jobType})`);
      deletePromises.push(deleteDoc(doc.ref));
    });
    
    await Promise.all(deletePromises);
    
    console.log(`✅ Successfully deleted ${snapshot.size} schedule(s) for invoice ${invoiceId}`);
    
    return { 
      success: true, 
      deletedCount: snapshot.size,
      message: `Deleted ${snapshot.size} schedule(s)`
    };
    
  } catch (error) {
    console.error(`❌ Error deleting schedules for invoice ${invoiceId}:`, error);
    return { 
      success: false, 
      deletedCount: 0,
      error: error.message 
    };
  }
};

/**
 * Update schedule status
 * @param {string} scheduleId - Schedule ID
 * @param {string} newStatus - New status
 * @returns {Promise<boolean>} Success status
 */
export const updateScheduleStatus = async (scheduleId, newStatus) => {
  return updateSchedule(scheduleId, { status: newStatus });
};

/**
 * Get invoices without confirmed schedules (unconfirmed schedules)
 * @returns {Promise<Array>} Array of invoices without dates
 */
export const getUnconfirmedSchedules = async () => {
  try {
    const invoices = await getInvoices();
    const unconfirmed = [];

    for (const invoice of invoices) {
      let hasConfirmedDates = false;

      // Check if invoice has any confirmed dates using unified dates array
      if (invoice.dates && Array.isArray(invoice.dates) && invoice.dates.length > 0) {
        // Check if any date entry has a confirmed date (not TBC)
        hasConfirmedDates = invoice.dates.some(dateEntry => {
          if (dateEntry.type === 'date' && dateEntry.value) {
            return true; // Has confirmed single date
          }
          if (dateEntry.type === 'range' && dateEntry.value && dateEntry.endValue) {
            return true; // Has confirmed date range
          }
          return false; // TBC entries don't count as confirmed dates
        });

        // Check if there are any TBC dates that need attention
        const hasTbcDates = invoice.dates.some(dateEntry => dateEntry.type === 'tbc');

        console.log(`📅 Invoice ${invoice.invoiceNumber} has unified dates:`, {
          datesCount: invoice.dates.length,
          hasConfirmedDates: hasConfirmedDates,
          hasTbcDates: hasTbcDates,
          dates: invoice.dates
        });

        // If invoice has TBC dates, it should be considered unconfirmed regardless of confirmed dates
        if (hasTbcDates) {
          hasConfirmedDates = false; // Override to ensure it appears in unconfirmed list
          console.log(`⚠️ Invoice ${invoice.invoiceNumber} has TBC dates - marking as unconfirmed`);
        }
      } else {
        // Fallback to legacy date field checking for backward compatibility
        console.log(`📅 Invoice ${invoice.invoiceNumber} has no unified dates, checking legacy fields`);

        switch (invoice.invoiceType) {
          case 'rental':
          case 'rental_extension':
            hasConfirmedDates = !!(invoice.rentalStartDate && invoice.rentalEndDate);
            break;
          case 'purchase':
            hasConfirmedDates = !!invoice.leadTime;
            break;
          case 'scaffolding_service':
            hasConfirmedDates = !!invoice.serviceDate;
            break;
          case 'damaged_items':
            // CRITICAL FIX: Damaged item invoices should NEVER appear in schedules
            // They are handled separately and don't require scheduling
            hasConfirmedDates = true; // Always consider as "confirmed" to exclude from unconfirmed list
            console.log(`⏭️ Excluding damaged_items invoice ${invoice.invoiceNumber} from unconfirmed schedules`);
            break;
        }
      }

      if (!hasConfirmedDates) {
        unconfirmed.push({
          ...invoice,
          reason: 'No dates specified in invoice'
        });
      }
    }

    console.log(`📊 Found ${unconfirmed.length} unconfirmed invoices out of ${invoices.length} total`);
    return unconfirmed;
  } catch (error) {
    console.error('Error getting unconfirmed schedules:', error);
    return [];
  }
};

/**
 * Enhanced key generation for schedules that properly handles TBC entries
 */
const generateScheduleKey = (invoice, schedule, tbcIndex = 0) => {
  const baseKey = `${invoice.id}_${schedule.jobType}`;
  
  // For TBC schedules (unconfirmed with no date), use special TBC identifier
  if (schedule.status === 'unconfirmed' && (!schedule.invoiceDate || schedule.invoiceDate === null)) {
    return `${baseKey}_TBC_${tbcIndex}`;
  }
  
  // For confirmed schedules with dates
  if (schedule.invoiceDate) {
    return `${baseKey}_${schedule.invoiceDate}`;
  }
  
  // Fallback for legacy schedules
  if (schedule.jobDate) {
    return `${baseKey}_${schedule.jobDate}`;
  }
  
  // Final fallback with index
  return `${baseKey}_${tbcIndex}`;
};

/**
 * Enhanced key generation for expected schedules from invoice data
 */
const generateExpectedScheduleKey = (invoice, expectedSchedule, tbcIndex = 0) => {
  const baseKey = `${invoice.id}_${expectedSchedule.jobType}`;
  
  // For TBC entries (status unconfirmed, no date)
  if (expectedSchedule.status === 'unconfirmed' && (!expectedSchedule.invoiceDate || expectedSchedule.invoiceDate === null)) {
    return `${baseKey}_TBC_${tbcIndex}`;
  }
  
  // For confirmed schedules with dates
  if (expectedSchedule.invoiceDate) {
    return `${baseKey}_${expectedSchedule.invoiceDate}`;
  }
  
  // Fallback with index
  return `${baseKey}_${tbcIndex}`;
};

/**
 * Sync schedules from all invoices with enhanced TBC handling
 * This function should be called periodically or when invoices are updated
 * @returns {Promise<Object>} Sync results
 */
export const syncSchedulesFromInvoices = async () => {
  console.log('🔄 Starting enhanced schedule sync with TBC fix...');

  try {
    const invoices = await getInvoices();
    const existingSchedules = await getSchedules({ includePastDates: true });

    console.log(`📄 Found ${invoices.length} invoices to process`);
    console.log(`📅 Found ${existingSchedules.length} existing schedules`);

    // Debug: Log all invoices with their dates
    invoices.forEach(invoice => {
      console.log(`📄 Invoice ${invoice.invoiceNumber}:`, {
        id: invoice.id,
        invoiceType: invoice.invoiceType,
        dates: invoice.dates,
        hasDates: !!(invoice.dates && Array.isArray(invoice.dates) && invoice.dates.length > 0)
      });
    });

    // Filter invoices that have dates to process
    const invoicesWithDates = invoices.filter(invoice =>
      invoice.dates && Array.isArray(invoice.dates) && invoice.dates.length > 0
    );
    console.log(`🎯 Found ${invoicesWithDates.length} invoices with dates to process`);
    
    let created = 0;
    let updated = 0;
    let deleted = 0;
    let errors = 0;
    let skipped = 0;

    for (const invoice of invoices) {
      try {
        console.log(`\n🔍 Processing invoice: ${invoice.invoiceNumber}`);
        console.log(`🔍 Invoice details:`, {
          id: invoice.id,
          invoiceType: invoice.invoiceType,
          dates: invoice.dates,
          lineItems: invoice.lineItems
        });

        // Get all existing schedules for this invoice (regardless of job type)
        const invoiceSchedules = existingSchedules.filter(s => s.invoiceId === invoice.id);
        console.log(`📅 Found ${invoiceSchedules.length} existing schedules for this invoice`);

        // Generate what the schedules should be
        const generatedSchedules = generateSchedulesFromInvoice(invoice);
        console.log(`📊 Generated ${generatedSchedules.length} schedules for ${invoice.invoiceNumber}`);

        if (generatedSchedules.length === 0) {
          console.log(`⏭️ Skipped ${invoice.invoiceNumber} - no schedules generated`);

          // If no schedules should exist, delete any existing ones
          if (invoiceSchedules.length > 0) {
            console.log(`🗑️ Deleting ${invoiceSchedules.length} orphaned schedules`);
            for (const schedule of invoiceSchedules) {
              await deleteSchedule(schedule.id);
              deleted++;
            }
          }

          skipped++;
          continue;
        }
        
        // ENHANCED: Use improved key generation that handles TBC schedules properly
        const expectedScheduleMap = new Map();
        let expectedTbcCounter = 0;
        
        generatedSchedules.forEach((schedule, index) => {
          let key;
          if (schedule.status === 'unconfirmed' && (!schedule.invoiceDate || schedule.invoiceDate === null)) {
            // TBC schedule - use special key with counter
            key = generateExpectedScheduleKey(invoice, schedule, expectedTbcCounter);
            expectedTbcCounter++;
          } else {
            // Regular schedule with date
            key = generateExpectedScheduleKey(invoice, schedule, index);
          }
          expectedScheduleMap.set(key, schedule);
          console.log(`🔑 Generated expected schedule key: ${key} for ${schedule.status} schedule`);
        });
        
        // ENHANCED: Map existing schedules with improved key generation
        const existingScheduleMap = new Map();
        let existingTbcCounter = 0;

        invoiceSchedules.forEach((schedule, index) => {
          // Use invoiceId if available, fallback to matching by invoiceNumber
          const scheduleInvoiceId = schedule.invoiceId ||
            (schedule.invoiceNumber === invoice.invoiceNumber ? invoice.id : null);

          if (scheduleInvoiceId) {
            let key;
            if (schedule.status === 'unconfirmed' && (!schedule.invoiceDate || schedule.invoiceDate === null)) {
              // TBC schedule - use special key with counter
              key = generateScheduleKey(invoice, schedule, existingTbcCounter);
              existingTbcCounter++;
            } else {
              // Regular schedule with date
              key = generateScheduleKey(invoice, schedule, index);
            }

            existingScheduleMap.set(key, schedule);

            // Debug logging to track matching
            console.log(`🔍 Mapped existing schedule: ${schedule.id} -> key: ${key}`);
            console.log(`   Schedule status: ${schedule.status}`);
            console.log(`   Schedule invoiceDate: ${schedule.invoiceDate || 'null'}`);
            console.log(`   Schedule jobDate: ${schedule.jobDate || 'null'}`);
          } else {
            console.warn(`⚠️ Could not determine invoiceId for schedule ${schedule.id}`);
          }
        });
        
        // Find schedules to create, update, or delete
        const schedulesToCreate = [];
        const schedulesToUpdate = [];
        const schedulesToDelete = [];
        
        // Check what should exist vs what does exist
        for (const [key, expectedSchedule] of expectedScheduleMap) {
          if (existingScheduleMap.has(key)) {
            // Schedule exists with correct job type - update it
            const existingSchedule = existingScheduleMap.get(key);
            schedulesToUpdate.push({ existing: existingSchedule, expected: expectedSchedule });
          } else {
            // Schedule doesn't exist - create it
            schedulesToCreate.push(expectedSchedule);
          }
        }
        
        // Find schedules that shouldn't exist anymore
        for (const [key, existingSchedule] of existingScheduleMap) {
          if (!expectedScheduleMap.has(key)) {
            // CRITICAL PROTECTION: Never delete schedules with manual edits
            const hasManualEdits = existingSchedule.manualEdits?.scheduledDate || 
                                 existingSchedule.manualEdits?.scheduledTime || 
                                 existingSchedule.hasManualEdits ||
                                 (existingSchedule.editedFields && existingSchedule.editedFields.length > 0);
            
            if (hasManualEdits) {
              console.log(`🔒 PROTECTION: Preserving manually edited schedule ${existingSchedule.id} (${existingSchedule.jobDate} - ${existingSchedule.jobType})`);
              console.log(`   Manual edits detected:`, {
                scheduledDate: existingSchedule.manualEdits?.scheduledDate || false,
                scheduledTime: existingSchedule.manualEdits?.scheduledTime || false,
                hasManualEdits: existingSchedule.hasManualEdits || false,
                editedFields: existingSchedule.editedFields || []
              });
              // Do not add to deletion list - preserve this schedule
            } else {
              // This schedule shouldn't exist anymore and has no manual edits - safe to delete
              schedulesToDelete.push(existingSchedule);
            }
          }
        }
        
        // Handle special case: job type changed for same date
        // This happens when invoice services are modified
        const existingByDate = new Map();
        invoiceSchedules.forEach(schedule => {
          if (!existingByDate.has(schedule.jobDate)) {
            existingByDate.set(schedule.jobDate, []);
          }
          existingByDate.get(schedule.jobDate).push(schedule);
        });
        
        const expectedByDate = new Map();
        generatedSchedules.forEach(schedule => {
          if (!expectedByDate.has(schedule.jobDate)) {
            expectedByDate.set(schedule.jobDate, []);
          }
          expectedByDate.get(schedule.jobDate).push(schedule);
        });
        
        // Check for date conflicts (multiple schedules on same date with different job types)
        for (const [date, existingDateSchedules] of existingByDate) {
          const expectedDateSchedules = expectedByDate.get(date) || [];
          
          if (existingDateSchedules.length > 1 && expectedDateSchedules.length === 1) {
            // Multiple existing schedules for one expected - this is a duplicate issue
            console.log(`🔧 Fixing duplicate schedules on ${date}`);
            
            // Keep the most recently created one, delete the others
            const sortedExisting = existingDateSchedules.sort((a, b) => 
              (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0)
            );
            
            const scheduleToKeep = sortedExisting[0];
            const schedulesToRemove = sortedExisting.slice(1);
            
            // Update the kept schedule to match expected job type
            const expectedSchedule = expectedDateSchedules[0];
            schedulesToUpdate.push({ existing: scheduleToKeep, expected: expectedSchedule });
            
            // Mark others for deletion
            schedulesToRemove.forEach(schedule => {
              if (!schedulesToDelete.find(s => s.id === schedule.id)) {
                schedulesToDelete.push(schedule);
              }
            });
          }
        }
        
        // Execute the operations
        console.log(`📊 Operations for ${invoice.invoiceNumber}:`);
        console.log(`   Create: ${schedulesToCreate.length}`);
        console.log(`   Update: ${schedulesToUpdate.length}`);
        console.log(`   Delete: ${schedulesToDelete.length}`);
        
        // Create new schedules
        for (const scheduleData of schedulesToCreate) {
          console.log(`✨ Creating schedule: ${scheduleData.jobDate} - ${scheduleData.jobType}`);
          await createSchedule(scheduleData);
          created++;
        }
        
        // Update existing schedules using selective sync
        for (const { existing, expected } of schedulesToUpdate) {
          console.log(`🔄 Selective update for schedule: ${existing.jobDate} - ${existing.jobType}`);
          console.log(`🔍 DEBUG - Existing schedule data:`, {
            id: existing.id,
            jobDate: existing.jobDate,
            scheduledDate: existing.scheduledDate,
            scheduledTime: existing.scheduledTime,
            editedFields: existing.editedFields,
            hasManualEdits: existing.hasManualEdits,
            manualEdits: existing.manualEdits
          });
          
          // CRITICAL FIX: ABSOLUTELY NEVER pass scheduledDate or scheduledTime in sync operations
          // Create a completely clean expected object with ONLY invoice-related fields
          const cleanExpected = {
            invoiceId: expected.invoiceId,
            invoiceNumber: expected.invoiceNumber,
            invoiceDate: expected.invoiceDate, // This is the key field that should sync
            jobType: expected.jobType,
            customerName: expected.customerName,
            phone: expected.phone, // FIXED: Use 'phone' instead of 'customerPhone' to match protectableFields
            companyName: expected.companyName,
            contactPerson: expected.contactPerson,
            siteAddress: expected.siteAddress,
            configuration: expected.configuration,
            notes: expected.notes,
            status: expected.status,
            isManualEntry: expected.isManualEntry
            // CRITICAL: NEVER include scheduledDate, scheduledTime, or jobDate in sync operations
          };
          
          console.log(`🔍 DEBUG - Clean expected data (ONLY invoice fields):`, {
            invoiceDate: cleanExpected.invoiceDate,
            customerName: cleanExpected.customerName,
            jobType: cleanExpected.jobType,
            scheduledDate: 'NOT_INCLUDED',
            scheduledTime: 'NOT_INCLUDED'
          });
          
          // Use selective update with clean expected data (no scheduled fields)
          const success = await updateScheduleSelective(existing.id, existing, cleanExpected);
          if (success) {
            updated++;
            console.log(`✅ Successfully updated schedule ${existing.id} with selective sync`);
          } else {
            console.error(`❌ Failed to update schedule ${existing.id}`);
            errors++;
          }
        }
        
        // Delete orphaned schedules
        for (const schedule of schedulesToDelete) {
          console.log(`🗑️ Deleting orphaned schedule: ${schedule.jobDate} - ${schedule.jobType}`);
          await deleteSchedule(schedule.id);
          deleted++;
        }
        
      } catch (error) {
        console.error(`❌ Error syncing schedule for invoice ${invoice.invoiceNumber}:`, error);
        errors++;
      }
    }

    const result = { created, updated, deleted, errors, skipped, totalInvoices: invoices.length };
    
    console.log('\n📊 IMPROVED SYNC RESULTS:');
    console.log(`✨ Created: ${created} schedules`);
    console.log(`🔄 Updated: ${updated} schedules`);
    console.log(`🗑️ Deleted: ${deleted} schedules`);
    console.log(`⏭️ Skipped: ${skipped} invoices (no dates)`);
    console.log(`❌ Errors: ${errors} invoices`);
    console.log(`📄 Total invoices processed: ${invoices.length}`);

    return result;
  } catch (error) {
    console.error('❌ Error syncing schedules from invoices:', error);
    return { created: 0, updated: 0, deleted: 0, errors: 1, skipped: 0, totalInvoices: 0 };
  }
};

/**
 * Get schedule statistics
 * @param {Object} options - Options for filtering
 * @returns {Promise<Object>} Statistics object
 */
export const getScheduleStats = async (options = {}) => {
  try {
    const allSchedules = await getSchedules({ includePastDates: true, ...options });
    const unconfirmed = await getUnconfirmedSchedules();
    
    const today = new Date().toISOString().split('T')[0];
    const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    // Separate upcoming vs past/completed jobs
    const upcomingSchedules = allSchedules.filter(s => s.jobDate >= today);
    const pastSchedules = allSchedules.filter(s => s.jobDate < today);
    const completedSchedules = allSchedules.filter(s => s.status === SCHEDULE_STATUSES.COMPLETED);
    
    const stats = {
      // Main metrics - focus on actionable jobs
      upcoming: upcomingSchedules.length,
      today: allSchedules.filter(s => s.jobDate === today).length,
      tomorrow: allSchedules.filter(s => s.jobDate === tomorrow).length,
      thisWeek: 0,
      unconfirmed: unconfirmed.length,
      
      // Historical/status metrics
      completed: completedSchedules.length,
      past: pastSchedules.filter(s => s.status !== SCHEDULE_STATUSES.COMPLETED).length,
      
      // Legacy total for backward compatibility (but less prominent)
      total: allSchedules.length,
      
      // Status breakdown
      confirmed: allSchedules.filter(s => s.status === SCHEDULE_STATUSES.CONFIRMED).length,
      cancelled: allSchedules.filter(s => s.status === SCHEDULE_STATUSES.CANCELLED).length,
      
      // Job type breakdown
      byJobType: {}
    };

    // Calculate this week (upcoming jobs only)
    const startOfWeek = new Date();
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(endOfWeek.getDate() + 6);
    
    const startOfWeekStr = startOfWeek.toISOString().split('T')[0];
    const endOfWeekStr = endOfWeek.toISOString().split('T')[0];
    
    const thisWeekSchedules = upcomingSchedules.filter(s => 
      s.jobDate >= startOfWeekStr && s.jobDate <= endOfWeekStr
    );
    
    stats.thisWeek = thisWeekSchedules.length;

    // Count by job type (upcoming jobs only)
    Object.values(JOB_TYPES).forEach(jobType => {
      const jobTypeSchedules = upcomingSchedules.filter(s => s.jobType === jobType);
      stats.byJobType[jobType] = {
        count: jobTypeSchedules.length
      };
    });

    return stats;
  } catch (error) {
    console.error('Error getting schedule statistics:', error);
    return {
      upcoming: 0,
      today: 0,
      tomorrow: 0,
      thisWeek: 0,
      unconfirmed: 0,
      completed: 0,
      past: 0,
      total: 0,
      confirmed: 0,
      cancelled: 0,
      byJobType: {}
    };
  }
};
